import React, {
    lazy,
    Suspense,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import GrowthAnalysis from 'components/pages/GrowthAnalysis';
import Navbar from 'components/organism/Navbar';
import Filter from 'components/organism/Filter';
import PromoScorecard from 'components/pages/PromoScorecard';
import BumpReport from 'components/pages/BumpReport';
import PromoLandscape from 'components/pages/PromoLandscape';
import Login from 'components/pages/Login';
import PortfolioAnalysis from 'components/pages/PortfolioAnalysis';
import GlobalProvider from 'contexts';
import PromoRecommendation from 'components/pages/PromoRecommendation';
import { PromoRecommendationContext } from 'contexts/PromoRecommendationContext';
import PriceLadder from 'components/pages/PriceLadder';
import OpportunityIdentification from 'components/pages/OportunityIdentification';
import PriceCurve from 'components/pages/PriceCurve';
import PriceChange from 'components/pages/PriceChange';
import PricePoint from 'components/pages/PricePoint';
import PriceTracking from 'components/pages/PriceTracking';
import { PriceSimulatorContext } from 'contexts/PriceSimulatorContext';
import { PricingSimulatorContext } from 'contexts/PricingSimulatorContext';
import PriceSimulator from 'components/pages/PriceSimulator';
import { ConfigContext, UserContext } from 'contexts/ConfigContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { LOGOUT_USER } from 'utils/mixpanelEventNames';
import PoweredByFooter from 'components/organism/Generic/PoweredByFooter';
import { POWERED_BY_FOOTER_HEIGHT_PX } from 'config/constants';
import {
    endAskAsperSession,
    ASK_ASPER_SESSION_END_REASON,
} from 'components/pages/AskAsper3/AskAsper/utils/askAsperSessionTracking';
import MainLogo from 'assets/images/logo.png';
import PVMAnalysis from 'components/pages/PVMAnalysis';
import Depletion from 'components/pages/Depletion';
import axios from 'axios';
import {  getConfig, handleTokenErrorAndRedirect } from 'utils/helperMethods';
import {
    clearPostLoginRedirect,
    getPostLoginRedirect,
    savePostLoginRedirect,
} from 'utils/postLoginRedirect';
import CommercialForecast from 'components/pages/CommercialForecast';
import { PricingSimulator } from 'components/pages';
import { UpdateOrViewPricingSimulator } from 'components/organism/PricingSimulator';
import UpdateOrViewPricingAnalytics from 'components/organism/PricingSimulator/UpdateOrViewPricingAnalytics';
import { PricingAnalyticsContext } from 'contexts/PricingAnalyticsContext';
import { CreatePriceSimulator } from 'components/organism';
import { TPOContext } from 'contexts/TPOContext';
import TPOPage from 'components/pages/TPOPage';
import UpdateOrViewTPO from 'components/organism/TradePromoOptimization/UpdateOrViewTPO';
import { PricingSimulatorWrapperContext } from 'contexts/PricingSimulatorWrapperContext';
import AppVersionRefresh from 'components/AppVersionRefresh';
import ProductFruitHelpdesk from 'components/molecules/ProductFruitHelpdesk';
import Loader from 'components/molecules/Loader';
import GlobalFilterRibbon from 'components/molecules/Filter/GlobalFilterRibbon';

import BestWeeksSection from 'components/template/PromoRecommendation/BestWeeksSection';
import { webAuth, ensureFreshToken } from 'services/tokenManager';
import { installFetchAuthInterceptor } from 'services/installFetchAuthInterceptor';
import useTokenRenewal from 'hooks/general/useTokenRenewal';
import { ThemeProvider } from '@mui/material';
import { ConfigProvider as AntdV5ConfigProvider, notification as antdV5Notification } from 'antd-v5';
import theme from 'globals/themes/theme';
import { AskAsper as AskAsper3 } from 'components/pages/AskAsper3/AskAsper/AskAsper';
import { getAskAsperPortalRoot } from 'components/pages/AskAsper3/AskAsper/utils/askAsperScope';
import ProfitPool from 'components/pages/ProfitPoolOverview/ProfitPool';
import ProfitPoolEvolution from 'components/pages/PPEvolution/ProfitPoolEvolution';
import GrowthMarginDrivers from 'components/pages/GrowthMarginDrivers/GrowthMarginDrivers';
import StrategicOpportunity from 'components/pages/StrategicOpportunity/StrategicOpportunity';
import GrowthMarginBridges from 'components/pages/GrowthMarginBridges/GrowthMarginBridges';
import { ProfitPoolOverviewContextProvider } from 'components/pages/ProfitPoolOverview/context/ProfitPoolOverviewContext';
import ShipmentGrowthAnalysis from 'components/pages/ShipmentGrowthAnalysis';
import ShipmentBumpReport from 'components/pages/ShipmentBumpReport';
import { ProfitPoolEvolutionContextProvider } from 'components/pages/PPEvolution/context/ProfitPoolEvolutionContext';
import { GrowthMarginDriversContextProvider } from 'components/pages/GrowthMarginDrivers/context/GrowthMarginDriversContext';
import { GrowthMarginBridgesProvider } from 'components/pages/GrowthMarginBridges/GrowthMarginBridgesContext';
import { isAdminUser, isSuperAdmin, canAccessAdminFeature, ADMIN_ROUTE_FEATURE_MAP } from 'constants/admin';
import { FilterContextData } from 'contexts/FilterContext';

import PPADashboard from 'components/pages/PPAHomePage';
import PpaScopeHydrator from 'components/pages/PpaScopeHydrator';
import PerformanceSummaryPage from 'components/pages/PerformanceSummary/PerformanceSummary';
import PackArchitecturePage from 'components/pages/PackArchitecture/PackArchitecture';
import PriceSimulatorV3 from 'components/pages/PriceSimulatorV3';
import PriceSimulatorV3ProjectScenarios from 'components/pages/PriceSimulatorV3ProjectScenarios';
import WhitespaceAnalysis from 'components/pages/WhitespaceAnalysis';
import { PriceSimulatorV3ContextWrapper } from 'contexts/PriceSimulatorV3ContextWrapper';
import { WhitespaceAnalysisContextWrapper } from 'contexts/WhitespaceAnalysisContext';
import { PackArchitectureContextWrapper } from 'contexts/PackArchitectureContext';
//DEFINE ALL APPLICATION ROUTES HERE
//WHENEVER A NEW ROUTE IS ADDED, ADD IT IN CONFIG ALSO (config keys:screens,screenDetailsWithRoutes )

const AdminSettings = lazy(() => import('components/pages/AdminSettings'));
const CacheManagement = lazy(() => import('components/pages/CacheManagement'));
const ForceRefresh = lazy(() => import('components/pages/ForceRefresh'));
const SimulatorValidation = lazy(() => import('components/pages/SimulatorValidation'));
const PPASimulatorAdmin = lazy(() => import('components/pages/PPASimulatorAdmin'));
const PPAConfigValidation = lazy(() => import('components/pages/PPAConfigValidation'));
const AppLightup = lazy(() => import('components/pages/AppLightup'));
const MigrationHistory = lazy(() => import('components/pages/MigrationHistory'));
const DERefreshJobs = lazy(() => import('components/pages/DERefreshJobs'));
const EvalManagementPage = lazy(() =>
    import('components/pages/AskAsper3/AskAsper/eval/EvalManagementPage').then((m) => ({
        default: m.EvalManagementPage,
    })),
);

//pageId-component mapping
const routeComponentMapping: any = {
    pvm: <PVMAnalysis />,
    ga: <GrowthAnalysis from="growth-analysis" />,
    sga: <ShipmentGrowthAnalysis />,
    br: <BumpReport from="bump-report" />,
    sbr: <ShipmentBumpReport />,
    da: <Depletion />,
    pcn: <PriceChange />,
    pp: <PricePoint />,
    pld: <PriceLadder />,
    pc: <PriceCurve />,
    pt: <PriceTracking />,
    oi: <OpportunityIdentification />,
    ps: <PromoScorecard />,
    pl: <PromoLandscape />,
    pa: <PortfolioAnalysis />,
    bw: <BestWeeksSection />,
    pr: (
        <PromoRecommendationContext>
            <PromoRecommendation />
        </PromoRecommendationContext>
    ),
    psr: <PriceSimulator />,
    psrNew: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    psrView: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    psrEdit: (
        <PriceSimulatorContext>
            <CreatePriceSimulator />
        </PriceSimulatorContext>
    ),
    cf: <CommercialForecast />,
    // v2
    psrV2: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <PricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psrV2View: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <UpdateOrViewPricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psrV2Update: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <UpdateOrViewPricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psa: (
        <PricingSimulatorWrapperContext>
            <PricingSimulatorContext>
                <PricingSimulator />
            </PricingSimulatorContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psaUpdate: (
        <PricingSimulatorWrapperContext>
            <PricingAnalyticsContext>
                <UpdateOrViewPricingAnalytics />
            </PricingAnalyticsContext>
        </PricingSimulatorWrapperContext>
    ),
    // v2
    psaView: (
        <PricingSimulatorWrapperContext>
            <PricingAnalyticsContext>
                <UpdateOrViewPricingAnalytics />
            </PricingAnalyticsContext>
        </PricingSimulatorWrapperContext>
    ),
    tpo: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    plan: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    viewUpdatePlan: (
        <TPOContext>
            <UpdateOrViewTPO />
        </TPOContext>
    ),
    bestWeeksTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    playbookTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    promoLibraryTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    eventLibraryTool: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    report: (
        <TPOContext>
            <TPOPage />
        </TPOContext>
    ),
    ppo: (
        <ProfitPoolOverviewContextProvider>
            <ProfitPool />
        </ProfitPoolOverviewContextProvider>
    ),
    ppevo: (
        <ProfitPoolEvolutionContextProvider>
            <ProfitPoolEvolution />
        </ProfitPoolEvolutionContextProvider>
    ),
    gmd: (
        <GrowthMarginDriversContextProvider>
            <GrowthMarginDrivers />
        </GrowthMarginDriversContextProvider>
    ),
    gmb: (
        <GrowthMarginBridgesProvider>
            <GrowthMarginBridges />
        </GrowthMarginBridgesProvider>
    ),
    adminIndex: <AdminSettings />,
    so: <StrategicOpportunity />,
    cm: <CacheManagement />,
    fr: <ForceRefresh />,
    sv: <SimulatorValidation />,
    ppaSim: <PPASimulatorAdmin />,
    pcv: <PPAConfigValidation />,
    al: <AppLightup />,
    mh: <MigrationHistory />,
    drj: <DERefreshJobs />,
    ppaHome: <PPADashboard />,
    ppaPerfSummary: <PerformanceSummaryPage />,
    wa: (
        <WhitespaceAnalysisContextWrapper>
            <WhitespaceAnalysis />
        </WhitespaceAnalysisContextWrapper>
    ),
    ppaPackArchitecture: (
        <PackArchitectureContextWrapper>
            <PackArchitecturePage />
        </PackArchitectureContextWrapper>
    ),
    psrV3: (
        <PriceSimulatorV3ContextWrapper>
            <PriceSimulatorV3 />
        </PriceSimulatorV3ContextWrapper>
    ),
    psrV3ProjectScenarios: (
        <PriceSimulatorV3ContextWrapper>
            <PriceSimulatorV3ProjectScenarios />
        </PriceSimulatorV3ContextWrapper>
    ),
};

// Keep raw fetch() calls (which attach their own Bearer token) on a fresh token,
// mirroring the axios request interceptor. Installed once at module load.
installFetchAuthInterceptor();

export default function App() {
    const navigate = useNavigate();
    const route = useLocation();
    // //fetching new token when old token expires.
    // oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
    //     oktaAuth.tokenManager.renew(key);
    // });
    const onLogout = useCallback(() => {
        trackEventMixpanel(LOGOUT_USER);
        // End an active Ask Asper session with the logout reason before storage is cleared,
        // so Mixpanel can still read the stored email/token. No-op if no session is active.
        endAskAsperSession(ASK_ASPER_SESSION_END_REASON.LOGOUT);
        webAuth.logout({ returnTo: window.location.origin });
        localStorage.clear();
    }, [webAuth]);

    //token renewal auth0
    useTokenRenewal();

    //to control visibility of JIRA support button and freshdesk widget
    useEffect(() => {
        const button = document.getElementById('jsd-widget') as HTMLElement;
        const freshdeskButton = document.getElementById('freshworks-container') as HTMLElement;
        //for freshdesk
        if (button && process.env.REACT_APP_JIRA_HELP !== 'true') {
            button.style.display = 'none';
            return;
        }

        //for jira
        if (button && route.pathname.length < 2) {
            button.style.visibility = 'hidden';
        } else if (button && process.env.REACT_APP_JIRA_HELP === 'true') {
            button.style.visibility = 'visible';
            //    button.style.display = 'block';
            button.style.bottom = '-8px';
            button.style.userSelect = 'none';
        }

        //for freshdesk
        if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID === 'null') {
            freshdeskButton.style.display = 'none';
            return;
        }
        if (route.pathname.length < 2 && freshdeskButton) {
            freshdeskButton.style.display = 'none';
        } else if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID !== 'null') {
            freshdeskButton.style.display = 'block';
            freshdeskButton.style.userSelect = 'none';
        }
    }, [route]);

    //to control visibility of JIRA support button and freshdesk widget
    useEffect(() => {
        const button = document.getElementById('jsd-widget') as HTMLElement;
        const freshdeskButton = document.getElementById('freshworks-container') as HTMLElement;
        //for freshdesk
        if (button && process.env.REACT_APP_JIRA_HELP !== 'true') {
            button.style.display = 'none';
            return;
        }

        //for jira
        if (button && route.pathname.length < 2) {
            button.style.visibility = 'hidden';
        } else if (button && process.env.REACT_APP_JIRA_HELP === 'true') {
            button.style.visibility = 'visible';
            //    button.style.display = 'block';
            button.style.bottom = '-8px';
            button.style.userSelect = 'none';
        }

        //for freshdesk
        if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID === 'null') {
            freshdeskButton.style.display = 'none';
            return;
        }
        if (route.pathname.length < 2 && freshdeskButton) {
            freshdeskButton.style.display = 'none';
        } else if (freshdeskButton && process.env.REACT_APP_FRESHDESK_WIDGET_ID !== 'null') {
            freshdeskButton.style.display = 'block';
            freshdeskButton.style.userSelect = 'none';
        }
    }, [route]);

    //this is to handle when user directly uses url/refreshes
    useEffect(() => {
        if (!route.pathname.includes('callback') && route.pathname !== '/') {
            const returnPath = `${window.location.pathname}${window.location.search}`;
            savePostLoginRedirect(returnPath);
            navigate('/', { state: { path: returnPath } });
        }
    }, []);

    //fetch auth token and add in header
    useEffect(() => {
        axios.interceptors.request.use(async function (config) {
            // Guarantees a non-expired token at request time: returns the cached
            // token when it still has life left, otherwise silently renews first.
            const token = await ensureFreshToken();
            config.headers.Authorization = `Bearer ${token}`;
            return config;
        });

        //intercepting errors
        axios.interceptors.response.use(undefined, (error) => {
            // if (typeof error.response?.data === 'object') return Promise.reject(error);
            const parsedError = error?.response;

            //condition to check if token is invalid
            //refresh page if token is invalid so that new token will be generated
            //this will redirect user to login page if session is expired
            handleTokenErrorAndRedirect(parsedError);

            return Promise.reject(error);
        });
    }, []);

    return (
        <div>
            <div>
                <Routes>
                    <Route
                        path="*"
                        element={
                            <ConfigContext>
                                {/* Wraps MainContainer so FilterContext is available for collapsible global filter ribbon */}
                                <GlobalProvider>
                                    <MainContainer onLogout={onLogout} />
                                </GlobalProvider>
                            </ConfigContext>
                        }
                    />
                    <Route
                        path="/"
                        element={
                            <ConfigContext>
                                <LoginContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                    <Route
                        path="/callback"
                        element={
                            <ConfigContext>
                                <LoginContainer onLogout={onLogout} />
                            </ConfigContext>
                        }
                    />
                </Routes>
            </div>
        </div>
    );
}

type LoginProps = {
    onLogout: Function;
};
//LOGIN COMPONENT
const LoginContainer = ({ onLogout }: LoginProps) => (
    <div className="h-full w-full">
        <Routes>
            <Route path="*" element={<Login onLogout={onLogout} webAuth={webAuth} />} />
        </Routes>
    </div>
);

// Path prefix for admin-only routes; non-admin users are redirected to home when visiting these
const ADMIN_PATH_PREFIX = '/admin';

// Wraps AskAsper routes with the antd-v5 + MUI theme scope they require
const AskAsperRouteScope = ({ children }: { children: React.ReactNode }) => {
    // All AskAsper overlays (modals, dropdowns, popovers, notifications) mount into a
    // single body-level portal root carrying `.askasper-scope`, so scoped design-library
    // styles apply and nothing is clipped by the in-route shell's overflow:hidden.
    useEffect(() => {
        antdV5Notification.config({ getContainer: getAskAsperPortalRoot });
        return () => {
            antdV5Notification.config({ getContainer: () => document.body });
        };
    }, []);

    return (
        <AntdV5ConfigProvider
            prefixCls="ant5"
            iconPrefixCls="ant5icon"
            getPopupContainer={getAskAsperPortalRoot}
            getTargetContainer={getAskAsperPortalRoot}
        >
            <ThemeProvider theme={theme}>
                <div
                    className="askasper-scope"
                    style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {children}
                </div>
            </ThemeProvider>
        </AntdV5ConfigProvider>
    );
};

// Path segments for routes that should hide the global filter ribbon
const ROUTES_WITHOUT_GLOBAL_FILTER = [
    'pricing-simulator',
    'pricing-analytics',
    'tpo',
    'admin',
    'cache-management',
    'simulator-validation',
    'price-simulator-v3',
    'ppa/home',
];

type MainRoutesProps = {
    appConfig: any;
    enableAskAsper: boolean;
    isEvalEnabled: boolean;
    accessibleScreenDetails: any;
};

// Routed content rendered inside MainContainer; extracted to keep MainContainer within size limits
const MainRoutes = ({ appConfig, enableAskAsper, isEvalEnabled, accessibleScreenDetails }: MainRoutesProps) => (
    <Suspense
        fallback={
            <div className="w-full bg-white" style={{ minHeight: 'calc(100vh - 100px)' }}>
                <Loader size="default" />
            </div>
        }
    >
        <Routes>
            {accessibleScreenDetails?.flatMap((screen: any) => {
                const baseRoute = (
                    <Route key={screen.path} path={screen.path} element={routeComponentMapping[screen.pageId]} />
                );

                // Adding subRoutes if present
                const subRoutes = screen.subRoutes?.flatMap((subRoute: any) => (
                    <Route
                        key={`${screen.path}/${subRoute.suffix}`}
                        path={`${screen.path}/${subRoute.suffix}`}
                        element={routeComponentMapping[subRoute?.pageId]}
                    />
                ));

                return [baseRoute, ...(subRoutes || [])];
            })}
            {enableAskAsper && (
                <Route
                    path="/ask-asper"
                    element={
                        <AskAsperRouteScope>
                            <AskAsper3 isEvalEnabled={isEvalEnabled} />
                        </AskAsperRouteScope>
                    }
                />
            )}
            {isEvalEnabled && (
                <Route
                    path="/ask-asper/eval"
                    element={
                        <AskAsperRouteScope>
                            <EvalManagementPage />
                        </AskAsperRouteScope>
                    }
                />
            )}
            <Route
                path="*"
                element={
                    <Navigate
                        to={accessibleScreenDetails?.[0]?.path ?? appConfig?.screenDetailsWithRoutes?.[0]?.path}
                    />
                }
            />
        </Routes>
    </Suspense>
);

//WHENEVER A NEW ROUTE IS ADDED, ADD IT IN CONFIG ALSO (config keys:screens,screenDetailsWithRoutes )
//ALL COMPONENTS EXCEPT LOGIN
const MainContainer = ({ onLogout }: any) => {
    const { appConfig } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { isGlobalFilterCollapsed, expandFilterBol } = useContext(FilterContextData);
    // Measure fixed ribbon height so content below is not covered (ribbon is position: fixed)
    const globalFilterRibbonRef = useRef<HTMLDivElement | null>(null);
    const [globalFilterRibbonSpacerPx, setGlobalFilterRibbonSpacerPx] = useState(44);

    const commonConfig = getConfig('commonConfig', appConfig);
    const isGlobalFilterCollapsable = commonConfig?.isGlobalFilterCollapsable || false;
    const enableAskAsper = commonConfig?.enableAskAsper === true;
    const isEvalEnabled = enableAskAsper && commonConfig?.isEvalEnabled === true;

    // Redirect non-admin users away from admin routes (e.g. direct URL / refresh)
    useEffect(() => {
        if (!location.pathname.startsWith(ADMIN_PATH_PREFIX)) return;

        if (!isAdminUser()) {
            const homePath = appConfig?.screenDetailsWithRoutes?.[0]?.path;
            navigate(homePath ? `/${homePath}` : '/', { replace: true });
            return;
        }

        if (!isSuperAdmin()) {
            const featureKey = ADMIN_ROUTE_FEATURE_MAP[location.pathname];
            if (featureKey && !canAccessAdminFeature(featureKey)) {
                navigate('/admin', { replace: true });
            }
        }
    }, [location.pathname, appConfig?.screenDetailsWithRoutes, navigate]);

    // Redirect when AskAsper is disabled but user navigates to /ask-asper directly
    useEffect(() => {
        if (!enableAskAsper && location.pathname.includes('ask-asper')) {
            const homePath = appConfig?.screenDetailsWithRoutes?.[0]?.path;
            navigate(homePath ? `/${homePath}` : '/', { replace: true });
        }
    }, [enableAskAsper, location.pathname, appConfig?.screenDetailsWithRoutes, navigate]);

    // After login, re-apply a stored deep link if an intermediate redirect sent the user home.
    useEffect(() => {
        if (!localStorage.getItem('isAuthenticated')) return;
        const pending = getPostLoginRedirect();
        if (!pending || pending === '/') return;
        const current = `${location.pathname}${location.search}`;
        if (current !== pending) {
            navigate(pending, { replace: true });
            clearPostLoginRedirect();
        }
    }, [location.pathname, location.search, navigate]);


    const removeGF =
        ROUTES_WITHOUT_GLOBAL_FILTER.some((segment) => location.pathname?.includes(segment)) ||
        (enableAskAsper && location.pathname?.includes('ask-asper'));

    const isAskAsper = enableAskAsper && location.pathname?.includes('ask-asper');

    // Whitelist top-level routes against `userAccessScope.data_metrics.forecast_sources.screens`
    // (same source the Navbar uses). An empty / undefined array means no filter — every
    // route in `screenDetailsWithRoutes` is rendered. Sub-routes are not filtered: if the
    // parent screen is allowed, all its sub-routes render. Matches Navbar.tsx semantics.
    const accessibleScreenDetails = useMemo(() => {
        const screenDetails = appConfig?.screenDetailsWithRoutes;
        if (!screenDetails) return [];

        let accessibleScreens: string[] | undefined;
        try {
            const userScope = JSON.parse(localStorage.getItem('userAccessScope') || '{}');
            accessibleScreens = userScope?.data_metrics?.forecast_sources?.screens;
        } catch {
            accessibleScreens = undefined;
        }

        if (!Array.isArray(accessibleScreens) || accessibleScreens.length === 0) {
            return screenDetails;
        }
        return screenDetails.filter((screen: any) => accessibleScreens!.includes(screen.pageId));
    }, [appConfig?.screenDetailsWithRoutes]);

    // Pin ribbon under navbar; left offset follows open sidebar width or full width when filter is collapsed
    const globalFilterRibbonFixedInset = useMemo(() => {
        const right = '1rem';
        const top = 'var(--navbar-height)';
        const zIndex = expandFilterBol ? 53 : 40;
        const transition = '500ms';
        if (removeGF && !isGlobalFilterCollapsable) {
            return { top, right, left: '1rem', zIndex, transition } as const;
        }
        if (isGlobalFilterCollapsed) {
            return {
                top,
                right,
                left: '0',
                zIndex,
                transition,
            } as const;
        }
        return {
            top,
            right,
            left: 'var(--global-filter-width)',
            zIndex,
            transition,
        } as const;
    }, [expandFilterBol, removeGF, isGlobalFilterCollapsed, isGlobalFilterCollapsable]);

    // Keep spacer height in sync when ribbon content or collapse state changes size
    useLayoutEffect(() => {
        const el = globalFilterRibbonRef.current;
        if (!el) {
            return undefined;
        }
        const sync = () => setGlobalFilterRibbonSpacerPx(el.getBoundingClientRect().height);
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        return () => ro.disconnect();
    }, [removeGF, isGlobalFilterCollapsed]);

    // Keep spacer height in sync when ribbon content or collapse state changes size
    useLayoutEffect(() => {
        const el = globalFilterRibbonRef.current;
        if (!el) {
            return undefined;
        }
        const sync = () => setGlobalFilterRibbonSpacerPx(el.getBoundingClientRect().height);
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        return () => ro.disconnect();
    }, [removeGF, isGlobalFilterCollapsed]);

    return (
        <>
            <PpaScopeHydrator />
            {/* App version refresh Component*/}
            <AppVersionRefresh />
            <ProductFruitHelpdesk />
            <Layout className={isAskAsper ? 'h-screen font-primary' : 'h-full font-primary'}>
                <Navbar logOut={onLogout} />
                <div style={isAskAsper ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}}>
                    {!removeGF && <Filter />}
                    <PoweredByFooter />
                    <div
                        id="main-container"
                        style={
                            isAskAsper
                                ? {
                                      // Reserve sticky PoweredByFooter; agentic layouts fill this shell at 100%
                                      // (their footerHeight prop uses 100vh and cannot nest under the navbar).
                                      height: `calc(100vh - var(--navbar-height) - ${POWERED_BY_FOOTER_HEIGHT_PX}px)`,
                                      marginTop: 'var(--navbar-height)',
                                      overflow: 'hidden',
                                      display: 'flex',
                                      flexDirection: 'column',
                                  }
                                : {
                                      minHeight: 'calc(100vh - 80px)',
                                      paddingBottom: POWERED_BY_FOOTER_HEIGHT_PX,
                                      transition: '500ms ease',
                                  }
                        }
                        className={
                            isAskAsper
                                ? ''
                                : removeGF
                                ? 'm-4 main-screen-margin-top'
                                : `mt-4 mr-4 mb-4 global-filter-margin-left ${
                                      isGlobalFilterCollapsable
                                          ? 'main-screen-margin-top-no-margin-top'
                                          : 'main-screen-margin-top'
                                  }`
                        }
                    >
                        {isGlobalFilterCollapsable && !removeGF ? (
                            <>
                                <div
                                    ref={globalFilterRibbonRef}
                                    className="fixed w-full"
                                    style={globalFilterRibbonFixedInset}
                                >
                                    <GlobalFilterRibbon />
                                </div>
                                {/* Reserves vertical space for the fixed ribbon so routed content starts below it */}
                                <div className="shrink-0" style={{ height: globalFilterRibbonSpacerPx }} aria-hidden />
                            </>
                        ) : null}
                        <MainRoutes
                            appConfig={appConfig}
                            enableAskAsper={enableAskAsper}
                            isEvalEnabled={isEvalEnabled}
                            accessibleScreenDetails={accessibleScreenDetails}
                        />
                    </div>
                </div>
            </Layout>
        </>
    );
};
